/**
 * @namespace ModuleLoader
 * @aka SWRNEU.ModuleLoader
 *
 * Zentrale Initialisierung aller Module nach Selector.
 */
SWRNEU.ModuleLoader = (function ($) {
  //Mit dieser Variable werden die Debug-Ausgaben in der Konsole ein-/ausgeschaltet,
  //da es sehr viele werden können. Hilfreich können sie aber durchaus sein.
  var enableDebugMode = true;

  /**
   * Registry aller über den ModuleLoader verwalteten Module.
   *
   * Jedes Modul wird einem CSS-Selektor zugeordnet und beschreibt,
   * wie passende Elemente initialisiert und wieder aufgeräumt werden.
   *
   * Struktur:
   * {
   *   selector: {
   *     constructor: function,
   *     observe: boolean,
   *     initAfterPjax: boolean,
   *     disposeAfterPjax: boolean
   *   }
   * }
   */
  var modules = {};

  // Gemeinsamer IntersectionObserver für alle Module,
  // die erst beim Sichtbarwerden initialisiert werden sollen.
  var observer = null;

  /**
   * Erzeugt aus einem CSS-Selektor einen gültigen Schlüssel
   * für die Speicherung von jQuery-Daten.
   *
   * Sonderzeichen werden durch Unterstriche ersetzt, damit
   * der Schlüssel sicher als Data-Key verwendet werden kann.
   */
  function sanitizeKey (selector) {
    return 'swr_' + selector.replace(/[^a-zA-Z0-9\-_]/g, '_');
  }

  /**
   * Registriert ein Modul für einen beliebigen CSS-Selektor.
   * Wird später ein Element gefunden, das diesem Selektor
   * entspricht, kann das Modul automatisch initialisiert werden.
   * Bereits registrierte Selektoren werden überschrieben.
   *
   * @param {string} selector
   *        Für welchen Selektor soll ein Modul registriert werden
   * @param {function} constructorFn
   *        Welches Modul soll registriert werden (jQueryInterface-Konstruktor)
   * @param {object} [options]
   *        {observe: boolean, initAfterPjax: boolean, disposeAfterPjax: boolean} optional
   */
  function register (selector, constructorFn, options) {
    options = options || {};
    modules[selector] = {
      constructor: constructorFn,
      dataKey: options.dataKey,
      observe: !!options.observe,
      initAfterPjax: !!options.initAfterPjax,
      disposeAfterPjax: !!options.disposeAfterPjax
    };
    //Falls dieses Modul nach dem initialen DOM-ready registriert wird,
    //und der erste Aufruf von init() schon gelaufen ist,
    //versuchen wir direkt zu initialisieren:
    try {
      //Durch debouncedInitAfterRegister wird sichergestellt, dass
      //der Aufruf von init() nicht zu oft passiert:
      debouncedInitAfterRegister(document, 'register');
    } catch (e) {
      //defensive: falls DOM noch nicht ready, ignorieren
    }
  }

  /**
   *
   * @param {string} selector
   *        Der Selektor einer Instanz.
   * @param {HTMLElement} instance
   *        Die Instanz eines Moduls, die initialisiert werden soll.
   *
   * Diese Funktion ist die zentralste Funktion im ganzen ModuleLoader.
   * Sie sorgt dafür, dass ein Modul:
   *
   * - genau einmal pro Instanz initialisiert wird (egal wie oft init() aufgerufen wird)
   * - sicher in try/catch läuft und Fehler sauber behandelt werden
   * - Instanzen am DOM gespeichert werden
   */
  function initElement (selector, instance) {
    var instanceKey = sanitizeKey(selector);
    var module = modules[selector];

    if (!$.data(instance, instanceKey)) {
      try {
        // Plugin initialisieren
        module.constructor._jQueryInterface.call($(instance));

        // Echte Plugin-Instanz übernehmen
        $.data(instance, instanceKey, $(instance).data(module.dataKey));

      } catch (err) {
        $.removeData(instance, instanceKey);

        console.error('ModuleLoader: Fehler beim Initialisieren von', selector, 'auf', instance, err);
      }
    }
  }

  /**
   * Stellt sicher, dass ein einziger IntersectionObserver existiert.
   * Erzeugt bei Bedarf den globalen IntersectionObserver.
   *
   * Der IntersectionObserver:
   * - beobachtet DOM-Elemente, denen Module zugeordnet wurden.
   * - initialisiert Module, sobald das Element sichtbar wird.
   * - entfernt danach die Beobachtung, um Ressourcen zu sparen.
   */
  function ensureObserver () {
    if (observer) {
      //Wenn es bereits einen IntersectionObserver gibt,
      //hören wir hier auf.
      if (enableDebugMode) {
        console.log('ModuleLoader: Observer bereits registriert.');
      }
      return;
    }

    if (!('IntersectionObserver' in window)) {
      //Wenn IntersectionObserver nicht existiert, hören wir hier auf.
      observer = null;
      return;
    }

    //Der gemeinsame Observer wird erstellt:
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          //Wenn ein Element im Viewport angekommen ist,
          //wird geschaut welche Module auf diesem Element
          //initialisiert werden sollen (welche Selektoren
          //auf das Element zutreffen).
          //Bei jedem beobachteten Element wurde zuvor über $.data() gemerkt
          //welche Modul-Selektoren zum Element gehören.
          var selectors = $.data(entry.target, 'swr.observeSelectors') || [];

          if (selectors.length) {
            for (var i = 0; i < selectors.length; i++) {
              var selector = selectors[i];
              if (enableDebugMode) {
                console.log('ModuleLoader: Element ist im Viewport:', entry.target);
              }
              //Für jedes zum Element gehörende Modul,
              //wird die initElement-Funktion aufgerufen,
              //die das jeweilige Modul für das Element initialisiert.
              initElement(selector, entry.target);
            }
            //Entfernt entsprechende Infos vom Element,
            //damit das Modul nicht mehrfach auf demselben
            //Element initialisiert wird.
            $.removeData(entry.target, 'swr.observeSelectors');
          }
          //Nach der Initialisierung, oder wenn kein Modul am Element
          //vermerkt war, wird das Element nicht mehr beobachtet.
          observer.unobserve(entry.target);
        }
      });
    }, {
      //100px vor dem eigentlichen Sichtbereich, löst der
      //Observer aus, damit der Init geschieht bevor das Element
      //komplett sichtbar ist.
      rootMargin: '100px',
      threshold: 0.01
    });
  }

  /**
   * Initialisiert alle registrierten Module innerhalb eines Containers.
   *
   * @param {HTMLElement|jQuery|Document} container
   *        Der Bereich des DOM, in dem nach Modulen gesucht werden soll.
   *        Kann das gesamte Dokument, ein einzelnes HTMLElement oder ein
   *        jQuery-Objekt sein.
   *
   * @param {string} [initEvent]
   *        Optionaler Parameter der angibt, wodurch die Initialisierung ausgelöst wurde.
   *        Mögliche Werte:
   *        - 'inital' -> normale Initialisierung (z. B. Seitenaufruf)
   *        - 'register' -> nach der Registrierung eines Moduls
   *        - 'pjax' -> Initialisierung nach PJAX-Load. Module mit initAfterPjax=false
   *                   werden dabei übersprungen.
   *        - 'newNode' -> MutationObserver entdeckt neue Knoten
   */
  function init (container, initEvent) {
    var $container = container && container.nodeType ? $(container) : $(container || document);

    //Alle Module werden im Container gesucht:
    Object.keys(modules).forEach(function (selector) {
      var module = modules[selector];

      if (!module.initAfterPjax && initEvent === 'pjax') {
        //Wenn nach PJAX-Navigation nicht erneut initialisiert
        //werden soll, hören wir hier auf.
        if (enableDebugMode) {
          console.log('ModuleLoader: Nicht neu initialisieren nach PJAX:', selector, module);
        }
        return;
      }

      var $found = $container.find(selector);

      //falls der Container selbst das Modul ist:
      if ($container.is(selector)) {
        $found = $found.add($container.filter(selector));
      }

      if ($found.length === 0) {
        //Keine Instanz des Moduls gefunden
        return;
      }

      if (enableDebugMode) {
        console.log('GEFUNDEN:', selector, 'in', $container);
      }

      // Für Module mit observe=true werden die gefundenen
      // Elemente zunächst vom IntersectionObserver überwacht.
      // Die eigentliche Initialisierung erfolgt erst, sobald
      // das Element sichtbar wird.
      if (module.observe && 'IntersectionObserver' in window) {
        ensureObserver();

        //Für jede Instanz des Moduls wird der Observer
        //eingesetzt.
        $found.each(function () {
          var instance = this;
          var instanceKey = sanitizeKey(selector);

          if ($.data(instance, instanceKey)) {
            //Wenn eine Instanz bereits beobachtet wird,
            //hören wir hier auf.
            if (enableDebugMode) {
              console.log('ModuleLoader: Observer beobachtet Instanz bereits:', instance);
            }
            return;
          }

          //Es wird gespeichert welche Module auf welche Instanz warten,
          //damit der IntersectionObserver später weiß, welche Module er
          //initialisieren soll, sobald das Element sichtbar wird.
          var existing = $.data(instance, 'swr.observeSelectors') || [];

          if (existing.indexOf(selector) === -1) {
            existing.push(selector);
            $.data(instance, 'swr.observeSelectors', existing);
          }

          if (observer) {
            try {
              if (enableDebugMode) {
                if (initEvent === 'newNode') {
                  console.log('ModuleLoader: Neuer Knoten entdeckt:', instance);
                }

                console.log('ModuleLoader: Observer beobachtet Instanz:', instance);
              }

              //Der Observer wird diese Instanz beobachten.
              //Genaueres zur Beobachtung in der Funktion
              //ensureObserver
              observer.observe(instance);
            } catch (e) {
              //Falls beim Beobachten etwas fehlschlägt,
              //wird die Instanz sofort initialisiert
              initElement(selector, instance);
            }
          } else {
            //Wenn es keinen IntersectionObserver gibt,
            //wird die Instanz sofort initialisiert
            initElement(selector, instance);
          }
        });
      } else {
        //Falls das Modul direkt initialisiert werden soll,
        //weil observe: false gesetzt ist:
        $found.each(function () {
          initElement(selector, this);
        });
      }
    });

    window.contentChangeViaPjax = false;
  }

  /**
   * Es gibt zwei debouncete Versionen von init:
   * - eine für das Registrieren von Modulen
   * - eine für den MutationObserver
   *
   * Dadurch wird verhindert, dass der MutationObserver die debouncete
   * Version für das Registrieren von Modulen überschreibt.
   *
   * @type {(function(): void)|*}
   */
  var debouncedInitAfterRegister = SWRNEU.Util.debounce(function () {
    return init.apply(null, arguments);
  }, 50);

  var debouncedInit = SWRNEU.Util.debounce(function () {
    return init.apply(null, arguments);
  }, 50);

  /**
   * @param {HTMLElement|jQuery|Document} container
   *        Der Bereich des DOM, in dem nach Modulen gesucht werden soll.
   *        Kann das gesamte Dokument, ein einzelnes HTMLElement oder ein
   *        jQuery-Objekt sein.
   *
   * Diese Funktion entfernt ALLE Modul-Instanzen innerhalb eines
   * Containers (oder direkt den Container selbst),
   * die über das ModuleLoader-System initialisiert wurden.
   *
   * Das bedeutet:
   * - Die Instanz wird aus jQuery-Daten entfernt
   * - Falls das Modul eine eigene dispose()-Methode besitzt, wird diese ausgeführt
   * - Lazy-Loading-Metadaten (swr.observeSelectors) werden ebenfalls gelöscht
   */
  function dispose (container) {
    var $container = container && container.nodeType ? $(container) : $(container || document);

    //Für JEDE registrierte Modul-Definition durchgehen
    Object.keys(modules).forEach(function (selector) {
      var module = modules[selector];
      var instanceKey = sanitizeKey(selector);

      if (!module.disposeAfterPjax) {
        //Wenn nach PJAX-Navigation nicht aufgeräumt
        //werden soll, hören wir hier auf.
        if (enableDebugMode) {
          console.log('ModuleLoader: Kein dispose nach PJAX:', selector, module);
        }
        return;
      }

      //Es wurden Elemente gefunden, die zu einem Modul gehören.
      $container.find(selector).each(function () {
        var instance = $.data(this, instanceKey);

        disposeInstance(instance, instanceKey, this);
      });

      //Der Container selbst ist ein Modul-Element
      if ($container.is(selector)) {
        var instance = $.data($container[0], instanceKey);

        disposeInstance(instance, instanceKey, $container[0]);
      }
    });

    //Hilfsfunktion
    function disposeInstance (instance, instanceKey, element) {
      if (instance && typeof instance.dispose === 'function') {
        //Falls eine Instanz des Moduls existiert und sie eine
        //dispose()-Methode besitzt, soll diese ausgeführt werden.
        try {
          if (enableDebugMode) {
            console.log('ModuleLoader: Dispose', instance);
          }

          instance.dispose();
        } catch (e) {
          // Fehler in dispose() sollen nicht den ModuleLoader abbrechen
        }
      }

      //Entferne Modul-Instanz aus jQuery-Daten
      $.removeData(element, instanceKey);

      //Entferne Lazy-Loading-Metadaten (Modul-zu-Element-Zuordnung)
      $.removeData(element, 'swr.observeSelectors');

      if (observer) {
        observer.unobserve(element);
      }
    }
  }

  /**
   * Beim initialen Seitenaufruf wird für das gesamte
   * Dokument die init-Funktion aufgerufen.
   */
  $(function () {
    //Kleiner Timeout um Race mit Module-Registrierungen zu minimieren
    window.setTimeout(function () {
      init(document, 'initial');
    }, 30);
  });

  /**
   * Beim PJAX:END wird für den container
   * die init-Funktion aufgerufen.
   */
  $(document).on('pjax:end', function (event, xhr, options) {
    if (window.isFractal) {
      options = {
        container: $('#content')
      };

      options.container.append('<div class="fractal-div"></div>');
    }

    if (options && options.container) {
      try {
        init(options.container, 'pjax');
      } catch (e) {
        console.error('ModuleLoader: INIT Fehler', e);
      }
    }
  });

  /**
   * Beim PJAX:beforeReplace werden die Modul-Instanzen
   * im container aufgeräumt.
   */

  $(document).on('pjax:beforeReplace', function (event, contents, options) {
    dispose(options.container);
  });

  /**
   * Beobachtet den Content-Bereich auf nachträglich
   * eingefügte DOM-Elemente.
   *
   * Neu hinzugefügte Knoten werden gesammelt und anschließend
   * über die debouncte init()-Funktion verarbeitet, damit
   * Module auch für dynamisch erzeugte Inhalte automatisch
   * initialisiert werden.
   **/
  if (window.MutationObserver) {
    var mutationObserver = new MutationObserver(function (mutations) {
      if (window.contentChangeViaPjax) {
        //Um zu verhindern, dass der MutationObserver nach einer PJAX-Navigation
        //den ausgetauschten Inhalt von #content initialisiert, wird hier
        //abgebrochen.
        if (enableDebugMode) {
          console.log('ModuleLoader: MutationObserver übersprungen nach PJAX.');
        }

        return;
      }

      for (var m = 0; m < mutations.length; m++) {
        var mutation = mutations[m];
        var added = mutation.addedNodes;

        for (var i = 0; i < added.length; i++) {
          var node = added[i];
          if (node.nodeType === 1) {
            try {
              debouncedInit(node, 'newNode');
            } catch (e) {
              console.error('ModuleLoader: INIT Fehler', e);
            }
          }
        }
      }
    });

    const $content = $('#content');

    if ($content.length !== 0) {
      mutationObserver.observe($content[0], {
        childList: true,
        subtree: true
      });
    }
  }

  //Öffentliche API
  return {
    register: register,
    initElement: initElement,
    dispose: dispose
  };

})(window.jQuery);
